import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { battlesApi } from '../../../api/battles';
import { battlePersonApi } from '../../../api/battlePerson';
import { battlePairsApi } from '../../../api/battlePairs';
import { api } from '../../../api/axios';
import {
    isWebSocketConnected,
    subscribeToBattle,
    subscribeToTournament,
    subscribeToBattlePairRound
} from '../../../api/websocket';

/**
 * Хук для загрузки основных данных баттла
 */
export const useBattleData = () => {
  const { id } = useParams();

  const [battle, setBattle] = useState(null);
  const [coverData, setCoverData] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [judges, setJudges] = useState([]);
  const [battleRules, setBattleRules] = useState([]);
  const [battleLimitations, setBattleLimitations] = useState([]);
  const [roundTracks, setRoundTracks] = useState({});
  const [roundRules, setRoundRules] = useState({});
  const [roundLimitations, setRoundLimitations] = useState({});
  const [roundPairs, setRoundPairs] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [coverLoading, setCoverLoading] = useState(false);
  const [participantLoading, setParticipantLoading] = useState(false);

  /** Загрузка баттла */
  const fetchBattle = useCallback(async () => {
    try {
      setLoading(true);
      const data = await battlesApi.get(id);
      setBattle(data);
      setError(null);

      if (data.coverImageId) {
        fetchCover(data.coverImageId);
      }
    } catch (err) {
      // Обработка ошибок для неавторизованных пользователей
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Для просмотра деталей баттла необходимо войти в систему');
      } else if (err.response?.status === 404) {
        setError('Баттл не найден');
      } else {
        setError('Не удалось загрузить информацию о баттле');
      }
      console.error('[useBattleData] Failed to fetch battle:', err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  /** Загрузка обложки */
  const fetchCover = useCallback(async (coverId) => {
    try {
      setCoverLoading(true);
      const coverInfo = await battlesApi.getCover(id);

      const { data: blob } = await api.get(`/files/${coverId}/download`, {
        responseType: 'blob',
      });

      const blobUrl = URL.createObjectURL(blob);

      setCoverData({
        ...coverInfo,
        coverUrl: blobUrl,
        blobUrl: blobUrl,
      });
    } catch (err) {
      if (err.message === 'У баттла нет обложки') {
        // Это ожидаемая ситуация - у баттла нет обложки
        setCoverData(null);
      } else {
        console.error('[useBattleData] Failed to fetch cover:', err.message);
        setCoverData(null);
      }
    } finally {
      setCoverLoading(false);
    }
  }, [id]);

  /** Загрузка участников */
  const fetchParticipants = useCallback(async () => {
    try {
      const data = await battlesApi.getParticipants(id);
      // Обработка ответа: может быть массив или объект с content
      const participantsList = Array.isArray(data) ? data : (data?.content || []);
      console.log('[useBattleData] Fetched participants:', participantsList.length, participantsList);
      setParticipants(participantsList);
    } catch (err) {
      console.error('[useBattleData] Failed to fetch participants:', err.message);
      setParticipants([]);
    }
  }, [id]);

  /** Загрузка раундов */
  const fetchRounds = useCallback(async () => {
    try {
      const data = await battlesApi.getRounds(id);
      setRounds(data || []);
    } catch (err) {
      console.error('[useBattleData] Failed to fetch rounds:', err.message);
    }
  }, [id]);

  /** Загрузка жюри */
  const fetchJudges = useCallback(async () => {
    try {
      setParticipantLoading(true);
      const params = { battleId: id };
      const data = await battlePersonApi.list(params);
      const allParticipants = data?.content || [];

      // Фильтруем на клиенте по роли JUDGE
      const judgesList = allParticipants.filter((p) => p.role === 'JUDGE');
      setJudges(judgesList);
    } catch (err) {
      console.error('[useBattleData] Failed to fetch judges:', err.message);
      setJudges([]);
    } finally {
      setParticipantLoading(false);
    }
  }, [id]);

  /**
   * Нормализация mediaUrl: сервер может вернуть абсолютный URL, путь от корня
   * или голый ID файла — приводим к рабочему виду для загрузки аудио плеером
   * (плеер добавляет только origin хоста, путь /api/... должен быть в значении)
   */
  const normalizeMediaUrl = (value) => {
    if (!value) return null;
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith('/')) return value;
    // Голый ID файла — используем эндпоинт скачивания аудио
    return `/api/files/download/audio/${value}`;
  };

  /** Загрузка треков раунда */
  const fetchRoundTracks = useCallback(async (roundId) => {
    try {
      const data = await battlesApi.getRoundTracks(id, roundId);
      let works = data || [];

      // WorkTrackResponse (work.track) не содержит mediaUrl — обогащаем треки
      // ссылками на аудио из GET /api/tracks?roundId=..., иначе плеер в списке
      // треков раунда не отображается. Текст трека (track.text) приходит
      // из основного ответа GET /battles/{id}/rounds/{roundId}/tracks
      try {
        const tracksData = await battlesApi.getTracks({ roundId, size: 100 });
        const mediaByTrackId = new Map(
          (tracksData?.tracks || [])
            .map((t) => [
              t.id,
              normalizeMediaUrl(t.mediaUrl) ||
                (t.fileId ? `/api/files/download/audio/${t.fileId}` : null),
            ])
            .filter(([, url]) => url)
        );
        if (mediaByTrackId.size > 0) {
          works = works.map((work) =>
            work.track && mediaByTrackId.has(work.track.id)
              ? { ...work, track: { ...work.track, mediaUrl: mediaByTrackId.get(work.track.id) } }
              : work
          );
        }
      } catch (err) {
        console.error(
          `[useBattleData] Failed to fetch media URLs for round ${roundId}:`,
          err.message
        );
      }

      setRoundTracks((prev) => ({ ...prev, [roundId]: works }));
    } catch (err) {
      console.error(
        `[useBattleData] Failed to fetch tracks for round ${roundId}:`,
        err.message
      );
    }
  }, [id]);

  /** Загрузка правил баттла */
  const fetchBattleRules = useCallback(async () => {
    try {
      const data = await battlesApi.getRules(id);
      setBattleRules(data || []);
    } catch (err) {
      console.error(`[useBattleData] Failed to fetch battle rules:`, err.message);
      setBattleRules([]);
    }
  }, [id]);

  /** Загрузка ограничений баттла */
  const fetchBattleLimitations = useCallback(async () => {
    try {
      const data = await battlesApi.getLimitations(id);
      setBattleLimitations(data || []);
    } catch (err) {
      console.error(
        `[useBattleData] Failed to fetch battle limitations:`,
        err.message
      );
      setBattleLimitations([]);
    }
  }, [id]);

  /** Загрузка правил раунда */
  const fetchRoundRules = useCallback(async (roundId) => {
    try {
      console.log(`[useBattleData] Fetching rules for round ${roundId}...`);
      const data = await battlesApi.getRoundRules(id, roundId);
      console.log(`[useBattleData] Received rules for round ${roundId}:`, data);
      setRoundRules((prev) => {
        const next = { ...prev, [roundId]: data || [] };
        console.log(`[useBattleData] Updated roundRules:`, next);
        return next;
      });
    } catch (err) {
      console.error(
        `[useBattleData] Failed to fetch rules for round ${roundId}:`,
        err.message
      );
      setRoundRules((prev) => ({ ...prev, [roundId]: [] }));
    }
  }, [id]);

  /** Загрузка ограничений раунда */
  const fetchRoundLimitations = useCallback(async (roundId) => {
    try {
      console.log(`[useBattleData] Fetching limitations for round ${roundId}...`);
      const data = await battlesApi.getRoundLimitations(id, roundId);
      console.log(`[useBattleData] Received limitations for round ${roundId}:`, data);
      setRoundLimitations((prev) => {
        const next = { ...prev, [roundId]: data || [] };
        console.log(`[useBattleData] Updated roundLimitations:`, next);
        return next;
      });
    } catch (err) {
      console.error(
        `[useBattleData] Failed to fetch limitations for round ${roundId}:`,
        err.message
      );
      setRoundLimitations((prev) => ({ ...prev, [roundId]: [] }));
    }
  }, [id]);

  /** Загрузка пар раунда */
  const fetchRoundPairs = useCallback(async (roundId) => {
    try {
      const data = await battlesApi.getRoundPairs(roundId);
      setRoundPairs((prev) => ({ ...prev, [roundId]: data?.content || [] }));
    } catch (err) {
      console.error(
        `[useBattleData] Failed to fetch pairs for round ${roundId}:`,
        err.message
      );
      setRoundPairs((prev) => ({ ...prev, [roundId]: [] }));
    }
  }, [id]);

  /** Загрузка участников для вкладки с фильтрами */
  const fetchParticipantsForTab = useCallback(
    async (filterRole = 'PARTICIPANT', filterStatus = '') => {
      try {
        setParticipantLoading(true);
        const params = { battleId: id };
        // Если статус не указан, не передаём его на сервер (получаем все статусы)
        if (filterStatus && filterStatus !== '') {
          params.status = filterStatus;
        }
        const data = await battlePersonApi.list(params);
        const allParticipants = data?.content || [];

        // Фильтруем на клиенте по роли
        const filtered = filterRole
          ? allParticipants.filter((p) => p.role === filterRole)
          : allParticipants;

        setParticipants(filtered);
      } catch (err) {
        console.error(
          '[useBattleData] Failed to fetch participants for tab:',
          err.message
        );
      } finally {
        setParticipantLoading(false);
      }
    },
    [id]
  );

  /** Initial load */
  useEffect(() => {
    fetchBattle();
    fetchParticipants();
    fetchRounds();
  }, [fetchBattle, fetchParticipants, fetchRounds]);

  /** Загрузка пар для раундов типа PAIR при загрузке раундов */
  useEffect(() => {
    if (rounds.length > 0) {
      rounds.forEach((round) => {
        if (round.type === 'PAIR') {
          fetchRoundPairs(round.id);
        }
      });
    }
  }, [rounds, fetchRoundPairs]);

  /** Загрузка треков для всех раундов при загрузке раундов */
  useEffect(() => {
    if (rounds.length > 0) {
      console.log('[useBattleData] Loading tracks for all rounds...');
      rounds.forEach((round) => {
        // Загружаем треки только если ещё не загружены
        if (!roundTracks[round.id]) {
          fetchRoundTracks(round.id);
        }
      });
    }
  }, [rounds, fetchRoundTracks, roundTracks]);

  /** WebSocket подписка на обновления баттла */
  useEffect(() => {
    if (!id || !isWebSocketConnected()) {
      console.log('[useBattleData] WebSocket не подключён, подписка отменена');
      return;
    }

    console.log('[useBattleData] Подписка на WebSocket события баттла:', id);

    // Подписка на события баттла
    const battleUnsubscribe = subscribeToBattle(id, (event) => {
      console.log('[useBattleData] Battle event:', event);

      // Обработка событий баттла
      switch (event?.eventType) {
        case 'BATTLE_UPDATED':
          // Обновляем данные баттла
          fetchBattle();
          break;
        case 'BATTLE_DELETED':
          setError('Этот баттл был удалён');
          break;
        case 'PARTICIPANT_JOINED':
        case 'PARTICIPANT_LEFT':
        case 'PARTICIPANT_APPROVED':
        case 'PARTICIPANT_REJECTED':
        case 'PARTICIPANT_DISQUALIFIED':
          // Обновляем список участников
          fetchParticipants();
          break;
        case 'ROUND_CREATED':
        case 'ROUND_UPDATED':
        case 'ROUND_DELETED':
          // Обновляем раунды
          fetchRounds();
          break;
        case 'TRACK_UPLOADED':
          // Обновляем треки конкретного раунда
          if (event.roundId) {
            fetchRoundTracks(event.roundId);
          }
          break;
        case 'VOTE_SUBMITTED':
        case 'VOTE_UPDATED':
          // Голосование обновилось - можно показать уведомление
          console.log('[useBattleData] Голосование обновлено');
          break;
        default:
          console.log('[useBattleData] Неизвестное событие:', event);
      }
    });

    // Подписка на события пар для каждого раунда
    const pairUnsubscribes = [];
    rounds.forEach((round) => {
      if (round.type === 'PAIR') {
        const unsubscribe = subscribeToBattlePairRound(round.id, (event) => {
          console.log('[useBattleData] BattlePair event:', event);

          switch (event?.eventType) {
            case 'DRAW_COMPLETED':
              // Жеребьёвка завершена - обновляем пары раунда
              fetchRoundPairs(round.id);
              fetchRounds(); // Обновляем раунды (могли измениться)
              break;
            case 'PAIR_CREATED':
            case 'PAIR_UPDATED':
            case 'PAIR_DELETED':
            case 'PAIR_ACTIVATED':
            case 'PAIR_FINISHED':
            case 'PAIR_DISQUALIFIED':
            case 'WINNER_ANNOUNCED':
              // Обновляем пары конкретного раунда
              fetchRoundPairs(round.id);
              break;
            case 'TRACK_UPLOADED':
              // Трек загружен в пару - обновляем треки
              if (event.roundId) {
                fetchRoundTracks(event.roundId);
              }
              break;
            default:
              console.log('[useBattleData] Неизвестное событие пары:', event);
          }
        });
        pairUnsubscribes.push(unsubscribe);
      }
    });

    // Подписка на события турнира (если баттл связан с турниром)
    let tournamentUnsubscribe = null;
    if (battle?.tournamentId) {
      tournamentUnsubscribe = subscribeToTournament(battle.tournamentId, (event) => {
        console.log('[useBattleData] Tournament event:', event);
        // Если турнир обновился, возможно нужно обновить и баттл
        fetchBattle();
      });
    }

    return () => {
      console.log('[useBattleData] Отписка от WebSocket событий');
      if (battleUnsubscribe) {
        battleUnsubscribe.unsubscribe();
      }
      pairUnsubscribes.forEach((unsub) => unsub && unsub.unsubscribe());
      if (tournamentUnsubscribe) {
        tournamentUnsubscribe.unsubscribe();
      }
    };
  }, [id, battle?.tournamentId, rounds]);

  /** Cleanup для blob URL */
  useEffect(() => {
    return () => {
      if (coverData?.blobUrl) {
        URL.revokeObjectURL(coverData.blobUrl);
      }
    };
  }, [coverData?.blobUrl]);

  return {
    // Data
    battle,
    coverData,
    participants,
    rounds,
    judges,
    battleRules,
    battleLimitations,
    roundTracks,
    roundRules,
    roundLimitations,
    roundPairs,

    // State
    loading,
    error,
    coverLoading,
    participantLoading,

    // Actions
    fetchBattle,
    fetchParticipants,
    fetchRounds,
    fetchJudges,
    fetchRoundTracks,
    fetchBattleRules,
    fetchBattleLimitations,
    fetchRoundRules,
    fetchRoundLimitations,
    fetchRoundPairs,
    fetchParticipantsForTab,

    setError,
    setParticipants,
    setRounds,
  };
};
